import {
  Component, EventEmitter, Output,
} from '@angular/core';
import { Store } from '@ngrx/store';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { selectCurrentSite } from '../../../features/site/site.selectors';
import { Site } from '../../../models/site';
import { InspectionModule } from '../../../enums/inspection-module';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { User } from '../../../models/user';
import { selectUser } from '../../../features/user/user.selectors';
import {
  selectVolumeSummary, selectVolumeTotal
} from '../../../features/volume/volume.selectors';
import {
  selectDetectionSummary, selectDetectionTotal, selectFilteredDetections
} from '../../../features/detection/detection.selectors';
import { Actions, ofType } from '@ngrx/effects';
import {
  clearVolumeSummary, loadVolumes,
  loadVolumeSummary, loadVolumeSummaryFailure,
  loadVolumeSummarySuccess
} from '../../../features/volume/volume.actions';
import {
  addDetectionEmergencySuccess, addDetectionSuccess,
  clearDetectionSummary, deleteDetectionSuccess,
  loadDetectionSummary,
  loadDetectionSummaryFailure,
  loadDetectionSummarySuccess
} from '../../../features/detection/detection.actions';
import {
  CreatePerreadPipelineDialog
} from '../../../shared/dialogs/create-perread-pipeline-dialog/create-perread-pipeline.dialog';
import {
  retrievePerreadPipeline,
  retrievePerreadPipelineFailure,
  retrievePerreadPipelineSuccess,
  startPerreadPipeline,
  startPerreadPipelineFailure,
  startPerreadPipelineSuccess
} from '../../../features/pipeline/pipeline.actions';
import {
  selectBatchTotal,
  selectCurrentReferenceBatch,
  selectCurrentReferenceBatchId
} from '../../../features/batch/batch.selectors';
import { first } from 'rxjs';
import {
  selectConfigDisplayCuts,
  selectConfigDisplayDetections,
  selectConfigDisplayElevations, selectConfigDisplayMarkers,
  selectConfigDisplayMeasures,
  selectConfigDisplayVolumes,
  selectConfigViewRepresentation
} from '../../../features/config/config.selectors';
import { ViewRepresentation } from '../../../enums/representation';
import {
  selectCurrentTilesetConfigId
} from '../../../features/tileset-config/tileset-config.selectors';
import {
  setConfigDisplayCuts,
  setConfigDisplayDetections,
  setConfigDisplayElevations, setConfigDisplayMarkers,
  setConfigDisplayMeasures,
  setConfigDisplayVolumes
} from '../../../features/config/config.actions';
import {
  selectAllTheoricalLayers
} from '../../../features/theorical-layer/theorical-layer.selectors';
import { InspectionModulePipe } from '../../../shared/pipes/module.pipe';
import { TheoricalLayer } from '../../../models/theorical-layer';
import { map } from 'rxjs/operators';
import {
  selectMeasureTotal
} from '../../../features/measure/measure.selectors';
import {
  selectCutTotal
} from '../../../features/cut/cut.selectors';
import {
  selectTrackerElevationTotal, selectTrackerImageTotal
} from '../../../features/tracker/tracker.selectors';
import { WsService } from '../../../services/ws.service';


@Component({
    selector: 'site-details-panel',
    templateUrl: './site-details-panel.component.html',
    styleUrls: ['./site-details-panel.component.scss'],
    standalone: false
})
export class SiteDetailsPanelComponent {

  site: Site | null | undefined;
  user: User | null | undefined;
  inspectionModule = InspectionModule;
  volumeSummary: any;
  isLoadingVolumeSummary: boolean = true;
  perreadPipelineAvailable: boolean = false;
  detectionSummary: any;
  isLoadingDetectionSummary: boolean = true;
  isPointCloudDisplayed: boolean = false;
  selectedViewRepresentation: number | undefined;
  @Output() close: EventEmitter<any> = new EventEmitter()

  readonly batchCount$ = this.store.select(selectBatchTotal);
  readonly filteredDetections$ = this.store.select(selectFilteredDetections);
  readonly detectionCount$ = this.store.select(selectDetectionTotal);
  readonly volumeCount$ = this.store.select(selectVolumeTotal);
  readonly measureCount$ = this.store.select(selectMeasureTotal);
  readonly cutCount$ = this.store.select(selectCutTotal);
  readonly elevationCount$ = this.store.select(selectTrackerElevationTotal);
  readonly markerCount$ = this.store.select(selectTrackerImageTotal);
  readonly dateFormat$ = this.store.select(selectUser).pipe(
    map(user =>
      user?.languageCode === 'fr'
        ? 'dd/MM/yyyy'
        : 'MM/dd/yyyy'
    )
  );

  constructor(private store: Store,
              private actions: Actions,
              private wsService: WsService,
              private dialog: MatDialog,
              private modulePipe: InspectionModulePipe) {
    this.handlePerreadPipeline();
    this.handleSite();
    this.handleUser();
    this.handleVolumeSummary();
    this.handleDetectionSummary();
    this.handleBatch();
    this.handleDetection();
    this.handleViewConfiguration();
    this.handleSiteAndBatch();
    this.handleTheoricalLayers();
  }

  private handleTheoricalLayers(): void {
    this.store.select(selectAllTheoricalLayers)
      .pipe(takeUntilDestroyed())
      .subscribe((theoricalLayers: Array<TheoricalLayer>) => {
        // TODO:
      });
  }

  private handleSiteAndBatch(): void {
    this.store.select(selectCurrentReferenceBatch)
      .pipe(takeUntilDestroyed())
      .subscribe((referenceBatch) => {
        if (this.site && referenceBatch) {
          if (this.modulePipe.transform(this.site, [InspectionModule.PERREAD])) {
            this.store.dispatch(loadVolumes({
              organizationId: this.site.organization,
              siteId: this.site.id,
              comparedBatchId: this.site.lastBatch,
              referenceBatchId: referenceBatch.id,
              threshold: 1,
              step: 1
            }));
            this.store.dispatch(loadVolumeSummary({
              organizationId: this.site.organization,
              siteId: this.site.id,
              comparedBatchId: this.site.lastBatch,
              referenceBatchId: referenceBatch.id
            }));
          }
        }
      });
  }

  private handleViewConfiguration(): void {
    this.store.select(selectConfigViewRepresentation)
      .pipe(takeUntilDestroyed())
      .subscribe((viewRepresentation: ViewRepresentation) => {
        this.selectedViewRepresentation = viewRepresentation;
        this.isPointCloudDisplayed = viewRepresentation == ViewRepresentation.POINT_CLOUD
          || viewRepresentation == ViewRepresentation.SPLITTED_POINT_CLOUD
          || viewRepresentation == ViewRepresentation.DIFFERENTIAL;
    });
  }

  private handleDetection(): void {
    this.actions.pipe(ofType(
      addDetectionSuccess,
      addDetectionEmergencySuccess,
      deleteDetectionSuccess
    ), takeUntilDestroyed())
      .subscribe(result => {
        if (this.site) {
          this.store.dispatch(loadDetectionSummary({
            organizationId: this.site.organization,
            siteId: this.site.id
          }));
        }
      });
  }

  private handlePerreadPipeline(): void {
    this.actions.pipe(ofType(
      retrievePerreadPipelineSuccess,
      startPerreadPipelineSuccess), takeUntilDestroyed())
      .subscribe(result => {
        this.perreadPipelineAvailable = !!(result['pipeline']);
      });
    this.actions.pipe(ofType(
      retrievePerreadPipelineFailure,
      startPerreadPipelineFailure), takeUntilDestroyed())
      .subscribe(result => {
        this.perreadPipelineAvailable = false;
      });
  }

  private handleBatch(): void {
    this.store.select(selectCurrentReferenceBatchId)
      .pipe(takeUntilDestroyed())
      .subscribe((referenceBatchId) => {
        if (referenceBatchId && this.site) {
          if (this.modulePipe.transform(this.site, [InspectionModule.PERREAD])) {
            this.store.dispatch(retrievePerreadPipeline({
              organizationId: this.site.organization,
              siteId: this.site.id,
              comparedBatchId: this.site.lastBatch,
              referenceBatchId: referenceBatchId
            }));
          }
        }
      });
  }

  private handleDetectionSummary(): void {
    this.store.select(selectDetectionSummary)
      .pipe(takeUntilDestroyed())
      .subscribe((summary) => {
        this.detectionSummary = summary;
      });
    this.actions.pipe(ofType(loadDetectionSummary), takeUntilDestroyed())
      .subscribe(result => {
        this.isLoadingDetectionSummary = true;
      });
    this.actions.pipe(ofType(loadDetectionSummarySuccess), takeUntilDestroyed())
      .subscribe(result => {
        this.isLoadingDetectionSummary = false;
      });
    this.actions.pipe(ofType(loadDetectionSummaryFailure), takeUntilDestroyed())
      .subscribe(result => {
        this.isLoadingDetectionSummary = false;
        this.store.dispatch(clearDetectionSummary());
      });
  }

  private handleVolumeSummary(): void {
    this.store.select(selectVolumeSummary)
      .pipe(takeUntilDestroyed())
      .subscribe((summary) => {
        this.volumeSummary = summary;
      });
    this.actions.pipe(ofType(loadVolumeSummary), takeUntilDestroyed())
      .subscribe(result => {
        this.isLoadingVolumeSummary = true;
      });
    this.actions.pipe(ofType(loadVolumeSummarySuccess), takeUntilDestroyed())
      .subscribe(result => {
        this.isLoadingVolumeSummary = false;
      });
    this.actions.pipe(ofType(loadVolumeSummaryFailure), takeUntilDestroyed())
      .subscribe(result => {
        this.isLoadingVolumeSummary = false;
        this.store.dispatch(clearVolumeSummary());
      });
  }

  private handleSite(): void {
    this.store.select(selectCurrentSite)
      .pipe(takeUntilDestroyed())
      .subscribe((site: Site | null | undefined) => {
        if (site) {
          this.site = site;
          this.wsService.subscribeTopic({
            "organization": this.site.organization,
            "site": this.site.id
          });
        }
      });
  }

  private handleUser(): void {
    this.store.select(selectUser)
      .pipe(takeUntilDestroyed())
      .subscribe((user: User | undefined) => {
        if (user) {
          this.user = user;
        }
      });
  }

  readonly haveManyBatch$ = this.store.select(selectBatchTotal)
    .pipe(map(batchCount => batchCount > 1));

  readonly isDisplayMeasuresChecked$ = this.store.select(selectConfigDisplayMeasures);
  readonly isDisplayCutsChecked$ = this.store.select(selectConfigDisplayCuts);
  readonly isDisplayElevationsChecked$ = this.store.select(selectConfigDisplayElevations);
  readonly isDisplayMarkersChecked$ = this.store.select(selectConfigDisplayMarkers);
  readonly isDisplayVolumesChecked$ = this.store.select(selectConfigDisplayVolumes);
  readonly isDisplayDetectionsChecked$ = this.store.select(selectConfigDisplayDetections);
  readonly selectedTilesetConfigId$ = this.store.select(selectCurrentTilesetConfigId);

  onClickDeselectSite() {
    if (this.site) {
      this.close.next(this.site);
    }

  }

  onClickLaunchPerreadPipeline() {
    const dialogRef: MatDialogRef<CreatePerreadPipelineDialog> = this.dialog.open(CreatePerreadPipelineDialog, {
      disableClose: false
    });
    dialogRef.afterClosed()
      .subscribe(result => {
        if(result) {
          this.store.select(selectCurrentReferenceBatchId)
            .pipe(first()).subscribe((batchId: string | undefined) => {
            if (batchId && this.site) {
              this.store.dispatch(startPerreadPipeline({
                organizationId: this.site.organization,
                siteId: this.site.id,
                comparedBatchId: this.site.lastBatch,
                referenceBatchId: batchId
              }));
            }
          });
        }
      });
  }


  onToggleChangeDisplayMeasures(event: any) {
    this.store.dispatch(setConfigDisplayMeasures({display: event.checked}));
  }

  onToggleChangeDisplayCuts(event: any) {
    this.store.dispatch(setConfigDisplayCuts({display: event.checked}));
  }

  onToggleChangeDisplayElevations(event: any) {
    this.store.dispatch(setConfigDisplayElevations({display: event.checked}));
  }

  onToggleChangeDisplayMarkers(event: any) {
    this.store.dispatch(setConfigDisplayMarkers({display: event.checked}));
  }

  onToggleChangeDisplayDetections(event: any) {
    this.store.dispatch(setConfigDisplayDetections({display: event.checked}));
  }

  onToggleChangeDisplayVolumes(event: any) {
    this.store.dispatch(setConfigDisplayVolumes({display: event.checked}));
  }

  protected readonly onvolumechange = onvolumechange;

  get hasDetections(): boolean {
    return (this.detectionSummary?.detectionCount ?? 0) > 0 && !this.isLoadingDetectionSummary;
  }

  get hasVolumes(): boolean {
    return (this.volumeSummary?.densityPeak ?? 0) > 0 && !this.isLoadingVolumeSummary;
  }
}
